import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/store/authStore';
import { userService } from '@/services/user/userService';
import { favoriteModuleKey, sanitizeFavoriteModuleKeys } from '@/utils/moduleLabels';

export function useFavoriteModules() {
  const { user } = useAuth();
  const [favoriteKeys, setFavoriteKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const prefs = await userService.getPreferences();
        if (!cancelled) {
          setFavoriteKeys(sanitizeFavoriteModuleKeys(prefs.favoriteModuleKeys));
        }
      } catch {
        if (!cancelled) setFavoriteKeys([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const isFavorite = useCallback(
    (systemName: string, moduleName: string) =>
      favoriteKeys.includes(favoriteModuleKey(systemName, moduleName)),
    [favoriteKeys]
  );

  const persistKeys = useCallback(async (next: string[], previous: string[]) => {
    setFavoriteKeys(next);
    try {
      const saved = await userService.updatePreferences({ favoriteModuleKeys: next });
      setFavoriteKeys(sanitizeFavoriteModuleKeys(saved.favoriteModuleKeys ?? next));
    } catch {
      setFavoriteKeys(previous);
    }
  }, []);

  const toggleFavorite = useCallback(async (systemName: string, moduleName: string) => {
    const key = favoriteModuleKey(systemName, moduleName);
    const previous = favoriteKeys;
    const next = previous.includes(key)
      ? previous.filter((item) => item !== key)
      : [...previous, key];
    await persistKeys(next, previous);
  }, [favoriteKeys, persistKeys]);

  const reorderFavorites = useCallback(async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= favoriteKeys.length || toIndex >= favoriteKeys.length) return;
    const previous = favoriteKeys;
    const next = [...previous];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    await persistKeys(next, previous);
  }, [favoriteKeys, persistKeys]);

  return { favoriteKeys, isFavorite, toggleFavorite, reorderFavorites, loading };
}
